/**
 * Consent configuration is sourced from `GET /config/consents/primary` and
 * `GET /config/consents/secondary`. Secondary version ids come from the API
 * response (`default-consent-config.json` on the backend); fallbacks match that file.
 */

import * as Sentry from '@sentry/react'
import { clientConfigLanguageForLocale } from '../../i18n/types'
import type { ConsentConfigReference, ConsentConfigResponse, SecondaryConsentRow } from './consentOptions/types'

/** Bundled default ids from family-portal-api `default-consent-config.json`. */
export const CONSENT_CONFIG_ID = 'family-portal-consent-config'
export const CONSENT_CONFIG_VERSION_ID = '2026-06-17'

const DEFAULT_SECONDARY_SECTION_TITLE = 'Secondary Consents'

export type ConsentToTestingEntry = {
  language: string
  title: string
  description?: string
  descriptionParagraphs?: string[]
  comparatorTitle?: string | null
  guardianProbandOnlyTitle?: string | null
  guardianDuoTrioTitle?: string | null
  guardianSiblingsTitle?: string | null
  guardianSiblingsAndSelfTitle?: string | null
  subjectSampleLabel?: string | null
  ownSampleLabel?: string | null
}

export type SecondaryConsentConfigEntry = {
  language: string
  id: string
  title: string
  description?: string
  descriptionParagraphs?: string[]
}

/** Response body from `GET /config/consents/primary` or `GET /config/consents/secondary`. */
export type ConsentConfigPayload = {
  configId?: string
  configVersionId?: string
  consentToTesting?: ConsentToTestingEntry[]
  secondaryConsent?: SecondaryConsentConfigEntry[]
}

export function consentConfigVersionIdsFromPayload(payload: ConsentConfigPayload | undefined): ConsentConfigReference {
  const configId = payload?.configId?.trim()
  const configVersionId = payload?.configVersionId?.trim()
  return {
    configId: configId || CONSENT_CONFIG_ID,
    configVersionId: configVersionId || CONSENT_CONFIG_VERSION_ID,
  }
}

function pickByLanguage<T extends { language: string }>(
  entries: T[],
  locale: string,
  consentArea: 'consentToTesting' | 'secondaryConsent'
): T[] {
  const preferred = clientConfigLanguageForLocale(locale)
  const matched = entries.filter((entry) => entry.language === preferred)
  if (matched.length > 0) return matched
  const english = entries.filter((entry) => entry.language === 'English')
  if (english.length === 0) return entries
  if (preferred !== 'English') {
    Sentry.captureEvent({
      message: `Consent copy fell back to English for ${consentArea}`,
      level: 'warning',
      tags: { feature: 'consent', locale, 'consent.area': consentArea },
    })
  }
  return english
}

export function consentToTestingFromConsentConfig(
  payload: ConsentConfigPayload | undefined,
  locale: string
): ConsentToTestingEntry | undefined {
  const entries = payload?.consentToTesting
  if (!Array.isArray(entries)) return undefined
  return pickByLanguage(entries, locale, 'consentToTesting')[0]
}

export function secondaryConsentFromConsentConfig(
  payload: ConsentConfigPayload | undefined,
  locale: string
): SecondaryConsentConfigEntry[] {
  const entries = payload?.secondaryConsent
  if (!Array.isArray(entries)) return []
  return pickByLanguage(entries, locale, 'secondaryConsent')
}

export function secondaryConsentRowsFromConsentConfig(
  payload: ConsentConfigPayload | undefined,
  locale: string
): SecondaryConsentRow[] {
  return secondaryConsentFromConsentConfig(payload, locale).map(
    ({ id, title, description, descriptionParagraphs }) => ({
      item: { id, title, description, descriptionParagraphs },
      sectionTitle: DEFAULT_SECONDARY_SECTION_TITLE,
    })
  )
}

export function buildConsentConfigResponse(
  payload: ConsentConfigPayload | undefined,
  locale: string
): ConsentConfigResponse {
  return {
    ...consentConfigVersionIdsFromPayload(payload),
    secondaryItems: secondaryConsentRowsFromConsentConfig(payload, locale),
  }
}
